/**
 * Pure data shaping for the charts (fitness trend, weekly load, power
 * curve, ride chart). Kept out of the components so it can be tested
 * without rendering SVG.
 */
import type { FitnessPoint, PowerCurvePoint, RideSample } from '../types/api.ts';

/** Chart ranges in days: 6 weeks, 3 months, 1 year (kept as strings for storage). */
export const RANGE_DAYS = ['42', '90', '365'] as const;
export type RangeDays = (typeof RANGE_DAYS)[number];

/** Monday (``YYYY-MM-DD``) of the ISO week holding ``date`` (``YYYY-MM-DD``). */
export function isoMonday(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const offset = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
}

export interface WeekLoad {
  /** Monday of the week, ``YYYY-MM-DD`` (the ``/weeks/:iso`` route key). */
  week: string;
  tss: number;
  hours: number;
}

/** TSS and hours per ISO week, oldest first, summed from daily points. */
export function weeklyLoad(points: readonly FitnessPoint[]): WeekLoad[] {
  const byWeek = new Map<string, WeekLoad>();
  for (const p of points) {
    const week = isoMonday(p.date);
    const row = byWeek.get(week) ?? { week, tss: 0, hours: 0 };
    row.tss += p.tss ?? 0;
    row.hours += (p.duration_sec ?? 0) / 3600;
    byWeek.set(week, row);
  }
  return [...byWeek.values()].map((w) => ({
    week: w.week,
    tss: Math.round(w.tss),
    hours: Math.round(w.hours * 10) / 10,
  }));
}

/** No rides in the range at all: nothing worth charting. */
export function fitnessIsEmpty(points: readonly FitnessPoint[]): boolean {
  return points.every((p) => !p.ctl && !p.atl && !p.tss);
}

/** ``+5``, ``-13``, ``0`` (rounded). */
export function formatSigned(value: number): string {
  const n = Math.round(value);
  return n > 0 ? `+${n}` : `${n}`;
}

export type PowerUnit = 'watts' | 'wkg';

export interface CurveRow {
  sec: number;
  value: number | null;
  previous: number | null;
  point: PowerCurvePoint | null;
}

function toUnit(watts: number, unit: PowerUnit, weightKg: number | null): number {
  if (unit === 'wkg' && weightKg) return Math.round((watts / weightKg) * 100) / 100;
  return Math.round(watts);
}

/** One row per duration with data in either period, shortest first. */
export function powerCurveRows(
  best: readonly PowerCurvePoint[],
  previous: readonly PowerCurvePoint[] | undefined,
  unit: PowerUnit,
  weightKg: number | null,
): CurveRow[] {
  const rows = new Map<number, CurveRow>();
  const rowFor = (sec: number) => {
    const row = rows.get(sec) ?? { sec, value: null, previous: null, point: null };
    rows.set(sec, row);
    return row;
  };
  for (const p of best) {
    const row = rowFor(p.sec);
    row.value = toUnit(p.watts, unit, weightKg);
    row.point = p;
  }
  for (const p of previous ?? []) rowFor(p.sec).previous = toUnit(p.watts, unit, weightKg);
  return [...rows.values()].sort((a, b) => a.sec - b.sec);
}

export type RideSeries = 'power' | 'hr' | 'cadence' | 'speed';
export const RIDE_SERIES: readonly RideSeries[] = ['power', 'hr', 'cadence', 'speed'];

export interface RideRow {
  elapsed: number;
  km: number | null;
  power: number | null;
  hr: number | null;
  cadence: number | null;
  /** km/h */
  speed: number | null;
  altitude: number | null;
  powerMax: number | null;
}

/** Chart rows from samples; a missing time channel falls back to the index. */
export function rideRows(samples: readonly RideSample[]): RideRow[] {
  return samples.map((s, i) => ({
    elapsed: s.elapsed_sec ?? i,
    km: s.distance_m == null ? null : Math.round(s.distance_m / 10) / 100,
    power: s.power,
    hr: s.hr,
    cadence: s.cadence,
    speed: s.speed_mps == null ? null : Math.round(s.speed_mps * 36) / 10,
    altitude: s.altitude_m,
    powerMax: s.power_max ?? null,
  }));
}

/** Series that have at least one value, in chip order. */
export function availableSeries(rows: readonly RideRow[]): RideSeries[] {
  return RIDE_SERIES.filter((key) => rows.some((r) => r[key] != null));
}

/**
 * The series to draw: the remembered choice limited to what the ride has.
 * When none of those exist (an HR-only ride with "power" remembered),
 * heart rate, else the first available series.
 */
export function visibleSeries(
  chosen: readonly RideSeries[],
  available: readonly RideSeries[],
): RideSeries[] {
  const shown = available.filter((s) => chosen.includes(s));
  if (shown.length > 0) return shown;
  if (available.includes('hr')) return ['hr'];
  return available.slice(0, 1);
}

/** Average and max of one series (nulls skipped); null when empty. */
export function seriesStats(
  rows: readonly RideRow[],
  key: RideSeries,
): { avg: number; max: number } | null {
  const values = rows.map((r) => r[key]).filter((v): v is number => v != null);
  if (values.length === 0) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return { avg: Math.round(sum / values.length), max: Math.round(Math.max(...values)) };
}
